import { Component, OnInit, AfterViewInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core'
import { trigger, style, transition, animate } from '@angular/animations' // NOSONAR
import { MatTableDataSource } from '@angular/material/table'
import { MatPaginator } from '@angular/material/paginator'
import { INotification, NotificationType } from '../../models/notification.models'
import { DialogService } from '../../../../../../apps/keycom/src/app/services/dialog.service'
import { ConfirmDialogComponent } from '../../../../../../apps/keycom/src/app/dialogs/confirm-dialog/confirm-dialog.component'
import { NotificationFormDialogComponent } from '../../dialogs/notification-form-dialog/notification-form-dialog.component'
import { NotificationService } from '../../../../../../apps/keycom/src/app/services/notification.service'
import { NotificationPollingService } from '../../../../../../apps/keycom/src/app/services/notification-polling.service'

@Component({
  selector: 'app-notification-list',
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  animations: [
    trigger('slideDown', [ // NOSONAR
      transition(':enter', [ // NOSONAR
        style({ height: '0', opacity: 0, overflow: 'hidden' }), // NOSONAR
        animate('200ms ease-in-out', style({ height: '*', opacity: 1, overflow: 'hidden' })), // NOSONAR
      ]),
      transition(':leave', [ // NOSONAR
        style({ overflow: 'hidden' }), // NOSONAR
        animate('200ms ease-in-out', style({ height: '0', opacity: 0 })), // NOSONAR
      ]),
    ]),
  ],
})
export class NotificationListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator

  displayedColumns = ['title', 'type', 'publishDate', 'lastModified', 'actions']
  dataSource = new MatTableDataSource<INotification>()

  isLoading = true

  readonly NotificationType = NotificationType
  readonly pageSizeOptions = [10, 25, 50]
  readonly pageSize = 10

  constructor(
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private notificationPollingService: NotificationPollingService,
  ) {}

  showAdvancedFilters = false
  activeChip: 'all' | 'today' | 'week' | 'month' = 'all'

  searchKeyword = ''
  dateFrom: Date | null = null
  dateTo: Date | null = null
  selectedType: NotificationType | '' = ''

  readonly typeOptions = [
    { value: NotificationType.ReleaseNotes, label: 'Release Notes' },
    { value: NotificationType.Outage, label: 'Outage' },
    { value: NotificationType.ImportantUpdate, label: 'Important Update' },
    { value: NotificationType.General, label: 'General' },
  ]

  ngOnInit(): void {
    this.dataSource.filterPredicate = (row: INotification, _filter: string) => {
      const keyword = this.searchKeyword.trim().toLowerCase()
      if (keyword) {
        const matchesKeyword =
          row.title.toLowerCase().includes(keyword) ||
          this.stripHtml(row.content).toLowerCase().includes(keyword)
        if (!matchesKeyword) return false
      }

      const publishDate = new Date(row.publishedOn)
      publishDate.setHours(0, 0, 0, 0)

      if (this.selectedType && row.type !== this.selectedType) return false

      if (this.dateFrom) {
        const from = new Date(this.dateFrom)
        from.setHours(0, 0, 0, 0)
        if (publishDate < from) return false
      }

      if (this.dateTo) {
        const to = new Date(this.dateTo)
        to.setHours(23, 59, 59, 999)
        if (publishDate > to) return false
      }

      return true
    }

    this.notificationService.getAll().subscribe({
      next: (notifications) => {
        this.dataSource.data = notifications
        this.isLoading = false
        this.cdr.markForCheck()
      },
      error: () => {
        this.isLoading = false
        this.cdr.markForCheck()
      },
    })
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator
  }

  onSearch(value: string): void {
    this.searchKeyword = value
    this.applyFilter()
  }

  clearSearch(): void {
    this.searchKeyword = ''
    this.applyFilter()
  }

  onDateFromChange(value: Date | null): void {
    this.activeChip = 'all'
    this.dateFrom = value
    this.applyFilter()
  }

  onDateToChange(value: Date | null): void {
    this.activeChip = 'all'
    this.dateTo = value
    this.applyFilter()
  }

  clearDateRange(): void {
    this.activeChip = 'all'
    this.dateFrom = null
    this.dateTo = null
    this.applyFilter()
  }

  clearAllFilters(): void {
    this.activeChip = 'all'
    this.dateFrom = null
    this.dateTo = null
    this.selectedType = ''
    this.searchKeyword = ''
    this.applyFilter()
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters
    this.cdr.markForCheck()
  }

  applyQuickFilter(chip: 'all' | 'today' | 'week' | 'month'): void {
    if (this.activeChip === chip) return
    this.activeChip = chip
    const now = new Date()
    if (chip === 'all') {
      this.dateFrom = null
      this.dateTo = null
    } else if (chip === 'today') {
      const start = new Date(now)
      start.setHours(0, 0, 0, 0)
      this.dateFrom = start
      this.dateTo = new Date(now)
    } else if (chip === 'week') {
      const weekAgo = new Date(now)
      weekAgo.setDate(weekAgo.getDate() - 7)
      weekAgo.setHours(0, 0, 0, 0)
      this.dateFrom = weekAgo
      this.dateTo = null
    } else {
      this.dateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      this.dateTo = new Date(now.getFullYear(), now.getMonth(), 0)
    }
    this.applyFilter()
  }

  onTypeChange(value: NotificationType | ''): void {
    this.selectedType = value
    this.applyFilter()
  }

  get hasDateRange(): boolean {
    return !!(this.dateFrom || this.dateTo)
  }

  private applyFilter(): void {
    const hasFilter = !!(this.searchKeyword.trim() || this.dateFrom || this.dateTo || this.selectedType)
    this.dataSource.filter = hasFilter ? 'active' : ''
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage()
    }
  }

  getTypeLabel(type: NotificationType): string {
    const labels: Record<NotificationType, string> = {
      [NotificationType.ReleaseNotes]: 'Release Notes',
      [NotificationType.Outage]: 'Outage',
      [NotificationType.ImportantUpdate]: 'Important Update',
      [NotificationType.General]: 'General',
    }
    return labels[type]
  }

  getTypeIcon(type: NotificationType): string {
    const icons: Record<NotificationType, string> = {
      [NotificationType.ReleaseNotes]: 'article',
      [NotificationType.Outage]: 'warning_amber',
      [NotificationType.ImportantUpdate]: 'campaign',
      [NotificationType.General]: 'info',
    }
    return icons[type]
  }

  stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return (doc.documentElement.textContent ?? '').replace(/\s+/g, ' ').trim()
  }

  onNewNotification(): void {
    const dialogRef = this.dialogService.open(NotificationFormDialogComponent, { notification: undefined })
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return
      this.notificationService.create({ title: result.title, type: result.type, content: result.content }).subscribe((created) => {
        this.dataSource.data = [created, ...this.dataSource.data]
        this.cdr.markForCheck()
      })
    })
  }

  onEdit(notification: INotification, event: MouseEvent): void {
    event.stopPropagation()
    const dialogRef = this.dialogService.open(NotificationFormDialogComponent, { notification })
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return
      this.notificationService
        .update(notification.id, { title: result.title, type: result.type, content: result.content, sendNotification: result.sendNotification })
        .subscribe((updated) => {
          this.dataSource.data = this.dataSource.data.map((n) => (n.id === updated.id ? updated : n))
          this.cdr.markForCheck()
        })
    })
  }

  onDelete(notification: INotification, event: MouseEvent): void {
    event.stopPropagation()
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      title: 'Delete Notification',
      message: `Are you sure you want to delete "${notification.title}"? This action cannot be undone.`,
      confirm: 'Delete',
      cancel: 'Cancel',
      showCancel: true,
      reasonRequired: false,
      confirmColor: 'warn',
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        this.notificationService.delete(notification.id).subscribe(() => {
          this.dataSource.data = this.dataSource.data.filter((n) => n.id !== notification.id)
          this.notificationPollingService.closeToast(notification.id)
          this.cdr.markForCheck()
        })
      }
    })
  }
}
